import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
} from "@material-ui/core";

import { upsertProductsImport } from "./productsSB.functions";

/**
 * The MatchProductsDialog component is a dialog that displays a list of unmatched products.
 * It presents each unmatched product as a Grid item with a Card, and inside the Card is
 * a Typography displaying the ID of the unmatched product and a Select with options
 * corresponding to the products in the productsMaster array. The Select is populated with
 * the value of the products_master_id of the unmatched product, and the user can select
 * a different product from the list of options. When the user clicks the "Update" button,
 * the component calls the upsertProductsImport function with the updated productsImport
 * array. If the upsert is successful, the component calls the handleAlert function with
 * a success message and closes the dialog. If there is an error, the component calls the
 * handleAlert function with an error message.
 *
 * @param {object} props - component props
 * @param {boolean} props.open - If true, the dialog is open.
 * @param {Function} props.closeDialog - A function to close the dialog.
 * @param {Array} props.productsMaster - An array of products to match against.
 * @param {Array} props.productsImport - An array of unmatched products.
 * @param {Function} props.handleSelect - A function to call when a product is selected.
 * @param {Function} props.handleAlert - A function to call when the upsert is successful or
 *                                 when there is an error.
 */
function MatchProductsDialog({
  open = false,
  closeDialog = () => {},
  productsMaster = [],
  productsImport = [],
  handleSelect = () => {},
  handleAlert = () => {},
}) {
  const productsMasterValueMap = productsMaster.map(item => ({
    value: item.id,
    label: item.display_name,
  }));

  const productsMasterMenuItemMap = productsMasterValueMap.map(item => (
    <MenuItem value={item.value} key={item.value}>
      {item.label}
    </MenuItem>
  ));

  const handleUpdate = async () => {
    try {
      const upsertProductsImportResult = await upsertProductsImport(
        productsImport
      );

      if (upsertProductsImportResult.error === 0) {
        handleAlert("Unmatched products updated", "success");
        closeDialog();
      } else {
        throw new Error(upsertProductsImportResult.error);
      }
    } catch (error) {
      handleAlert(error, "error");
    }
  };

  const importsMap = productsImport.map(item => {
    return (
      <Grid item key={item.id}>
        <Card>
          <CardContent>
            <Typography variant="body2">{item.id}</Typography>
            <Select
              value={item.products_master_id || ""}
              fullWidth
              variant="standard"
              name="product_match"
              onChange={e => handleSelect(item.id, e.target.value)}
              label="Select Product"
            >
              {productsMasterMenuItemMap}
            </Select>
          </CardContent>
        </Card>
      </Grid>
    );
  });
  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>Match Products</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={1}>
          {importsMap}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleUpdate}>Update</Button>
        <Button onClick={closeDialog} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MatchProductsDialog;
